import { AiFillNotification, AiOutlineEdit, AiOutlineMail, AiOutlinePhone } from 'react-icons/ai';
import { Link, useParams } from 'react-router-dom';
import { OwnerType, UserRole } from '../../../common/constants';
import { useApproveMerchantMutation, useGetMerchantByIdQuery } from '../merchant.api-slice';
import { useEffect, useState } from 'react';
import Breadcrumb from '../../../components/Breadcrumb';
import DataContainer from '../../stores/components/DefaultApiConfig/DataContainer';
import EditMerchant from '../components/EditMerchant';
import { FaLocationArrow } from 'react-icons/fa';
import LoadingSpinner from '../../../components/LoadingSpinner';
import MerchantDetailStatus from '../components/MerchantDetailStatus';
import RuleDataContainer from '../../stores/components/DefaultRule/RuleDataContainer';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { toast } from 'react-toastify';
import { useAuth } from '../../../app/hooks/useAuth';
import { useGetMerchantDashboardQuery } from '../../dashboard/dashboard-api-slice';
import { useTranslation } from 'react-i18next';
import NotifyMerchant from '../components/NotifyMerchant';

const MerchantDashboardPage: React.FC = () => {
  const { id } = useParams();
  const auth = useAuth();
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openNotifyModal, setOpenNotifyModal] = useState<boolean>(false);
  const { data: merchant, isLoading, isSuccess } = useGetMerchantByIdQuery(id ?? skipToken);
  const { data: merchantStats, isLoading: isStatsLoading } = useGetMerchantDashboardQuery(id ?? skipToken);
  const [approveMerchant, { isSuccess: isApproveSuccess }] = useApproveMerchantMutation();
  const { t } = useTranslation();

  const handleApproveStoreClick = async (): Promise<void> => {
    if (id != null) {
      await approveMerchant(id);
    }
  };

  const toggleEditModal = (): void => {
    setOpenEditModal(!openEditModal);
  };

  const toggleNotifyModal = (): void => {
    setOpenNotifyModal(!openNotifyModal);
  };

  useEffect(() => {
    if (isApproveSuccess) {
      toast('Merchant is now approved.');
    }
  }, [isApproveSuccess]);

  if (isLoading || isStatsLoading || id == null) {
    return <LoadingSpinner />;
  }

  if (isSuccess && merchant === undefined) {
    toast('No such merchant exist');
    return <></>;
  }

  const stats = [
    {
      id: 'storeCount',
      title: 'Store',
      count: merchantStats?.storeCount != null ? merchantStats.storeCount : 0,
      link: `/admin/merchants/${id}/stores`
    },
    {
      id: 'userCount',
      title: 'User',
      count: merchantStats?.userCount != null ? merchantStats.userCount : 0,
      link: `/admin/merchants/${id}/users`
    },
    {
      id: 'deviceCount',
      title: 'Devices',
      count: merchantStats?.deviceCount != null ? merchantStats.deviceCount : 0,
      link: `/admin/merchants/${id}/devices`
    },
    {
      id: 'ruleCount',
      title: 'Receipt Rule',
      count: merchantStats?.receiptRuleCount != null ? merchantStats.receiptRuleCount : 0,
      link: ''
    }
  ];

  return (
    <>
      {merchant != null && <EditMerchant item={merchant} isOpen={openEditModal} toggleEditModal={toggleEditModal} />}
      {merchant != null && (
        <NotifyMerchant item={merchant} isOpen={openNotifyModal} toggleNotifyModal={toggleNotifyModal} />
      )}
      {merchant != null && (
        <div>
          <div className="pb-4">
            <h1>Dashboard</h1>
            <Breadcrumb
              items={[
                { label: t('common.adminBreadcrumbLabel') },
                { label: t('merchants.list.breadcrumbLabel') },
                { label: t('merchants.dashboard.breadcrumbLabel') }
              ]}
            />
          </div>
          <div className="flex w-full">
            <div className="w-1/2 flex flex-col px-4 bg-gray-50 py-4 rounded-lg">
              <div className="w-full flex justify-between items-center pb-4 border-b-2">
                <span className="font-xl font-bold text-xl text-center">{merchant.name}</span>
                <div className="flex space-x-2">
                  <div
                    className="flex py-2 px-4 text-gray-50 bg-red-500 text-center rounded-lg"
                    onClick={toggleNotifyModal}
                  >
                    <AiFillNotification size={24} />
                    <button className="text-sm">Notify</button>
                  </div>
                  <div
                    className="flex py-2 px-4 text-gray-50 bg-[#1D8A43] text-center rounded-lg"
                    onClick={toggleEditModal}
                  >
                    <AiOutlineEdit size={24} />
                    <button className="text-sm">Edit</button>
                  </div>
                </div>
              </div>

              {/* Detail of Store */}
              <div className="w-full flex">
                <div className="w-1/2">
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <FaLocationArrow size={16} />
                      <span className="">{merchant.address}</span>
                    </div>
                  </div>
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <AiOutlineMail size={16} />
                      <span className="">{merchant.email}</span>
                    </div>
                  </div>
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <AiOutlinePhone size={16} />
                      <span className="">{merchant.phone}</span>
                    </div>
                  </div>
                </div>
                {/* Status */}
                <div className="w-1/2 pl-16">
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <MerchantDetailStatus status={merchant.isApproved} title="Approved" />
                      {auth.user != null && auth.user.role === UserRole.SUPER_ADMIN && !merchant.isApproved && (
                        <button
                          onClick={() => handleApproveStoreClick}
                          className="py-1 px-2 text-gray-50 bg-[#1D8A43] text-sm text-center rounded-md"
                        >
                          Approve
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between px-4 py-2">
                    <div className="flex space-x-4 justify-between items-center">
                      <MerchantDetailStatus status={merchant.isActive} title="Status" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="w-1/2 flex flex-col">
              <div className="w-full flex flex-col md:flex-row justify-between space-y-2 md:space-y-0  space-x-0 md:space-x-2 ml-2">
                {stats.slice(0, 2).map((stat, i) => {
                  return (
                    <div
                      key={i}
                      className={`sm:w-full md:w-1/${stats.length} h-24 px-4 py-2 flex flex-col justify-between bg-gray-100 rounded-md`}
                    >
                      <span className="text-4xl text-center px-2">{stat.count}</span>
                      <Link to={stat.link} className="text-md text-center hover:text-[#1D8A43]">
                        <span>{stat.title}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
              <div className="w-full flex flex-col md:flex-row justify-between space-y-2 md:space-y-0  space-x-0 md:space-x-2 m-2">
                {stats.slice(2, 4).map((stat) => {
                  return (
                    <div
                      key={stat.id}
                      className={`sm:w-full md:w-1/${stats.length} h-24 px-4 py-2 flex flex-col justify-between bg-gray-100 rounded-md`}
                    >
                      <span className="text-4xl text-center px-2">{stat.count}</span>
                      <Link to={stat.link} className="text-md text-center hover:text-[#1D8A43]">
                        <span>{stat.title}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex w-full space-x-2">
            <div className="w-1/2 flex flex-col px-4 bg-gray-50 py-4 rounded-lg mt-2">
              <RuleDataContainer ownerEntityType={OwnerType.MERCHANT} ownerEntityId={merchant.id} />
            </div>
            <div className="w-1/2 flex flex-col px-4 bg-gray-50 py-4 rounded-lg mt-2">
              <DataContainer ownerType={OwnerType.MERCHANT} ownerEntity={merchant} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MerchantDashboardPage;
